// Team grids for break spot sheets, shared by the Breaks page and the
// view-only break panel so the two build identical rows and CSVs.

const NFL_DIVISIONS = [
  { name: 'AFC North', teams: ['Baltimore Ravens', 'Cincinnati Bengals', 'Cleveland Browns', 'Pittsburgh Steelers'] },
  { name: 'NFC North', teams: ['Chicago Bears', 'Detroit Lions', 'Green Bay Packers', 'Minnesota Vikings'] },
  { name: 'AFC East', teams: ['Buffalo Bills', 'Miami Dolphins', 'New England Patriots', 'New York Jets'] },
  { name: 'NFC East', teams: ['Dallas Cowboys', 'New York Giants', 'Philadelphia Eagles', 'Washington Commanders'] },
  { name: 'AFC South', teams: ['Houston Texans', 'Indianapolis Colts', 'Jacksonville Jaguars', 'Tennessee Titans'] },
  { name: 'NFC South', teams: ['Atlanta Falcons', 'Carolina Panthers', 'New Orleans Saints', 'Tampa Bay Buccaneers'] },
  { name: 'AFC West', teams: ['Denver Broncos', 'Kansas City Chiefs', 'Las Vegas Raiders', 'Los Angeles Chargers'] },
  { name: 'NFC West', teams: ['Arizona Cardinals', 'Los Angeles Rams', 'San Francisco 49ers', 'Seattle Seahawks'] },
];

const NFL_TEAMS = [
  'Arizona Cardinals', 'Atlanta Falcons', 'Baltimore Ravens', 'Buffalo Bills', 'Carolina Panthers',
  'Chicago Bears', 'Cincinnati Bengals', 'Cleveland Browns', 'Dallas Cowboys', 'Denver Broncos',
  'Detroit Lions', 'Green Bay Packers', 'Houston Texans', 'Indianapolis Colts', 'Jacksonville Jaguars',
  'Kansas City Chiefs', 'Las Vegas Raiders', 'Los Angeles Chargers', 'Los Angeles Rams', 'Miami Dolphins',
  'Minnesota Vikings', 'New England Patriots', 'New Orleans Saints', 'New York Giants', 'New York Jets',
  'Philadelphia Eagles', 'Pittsburgh Steelers', 'San Francisco 49ers', 'Seattle Seahawks',
  'Tampa Bay Buccaneers', 'Tennessee Titans', 'Washington Commanders'
];
const NBA_TEAMS = [
  'Atlanta Hawks', 'Boston Celtics', 'Brooklyn Nets', 'Charlotte Hornets', 'Chicago Bulls',
  'Cleveland Cavaliers', 'Dallas Mavericks', 'Denver Nuggets', 'Detroit Pistons', 'Golden State Warriors',
  'Houston Rockets', 'Indiana Pacers', 'Los Angeles Clippers', 'Los Angeles Lakers', 'Memphis Grizzlies',
  'Miami Heat', 'Milwaukee Bucks', 'Minnesota Timberwolves', 'New Orleans Pelicans', 'New York Knicks',
  'Oklahoma City Thunder', 'Orlando Magic', 'Philadelphia 76ers', 'Phoenix Suns', 'Portland Trail Blazers',
  'Sacramento Kings', 'San Antonio Spurs', 'Toronto Raptors', 'Utah Jazz', 'Washington Wizards'
];
const MLB_TEAMS = [
  'Arizona Diamondbacks', 'Atlanta Braves', 'Baltimore Orioles', 'Boston Red Sox', 'Chicago Cubs',
  'Chicago White Sox', 'Cincinnati Reds', 'Cleveland Guardians', 'Colorado Rockies', 'Detroit Tigers',
  'Houston Astros', 'Kansas City Royals', 'Los Angeles Angels', 'Los Angeles Dodgers', 'Miami Marlins',
  'Milwaukee Brewers', 'Minnesota Twins', 'New York Mets', 'New York Yankees', 'Oakland Athletics',
  'Philadelphia Phillies', 'Pittsburgh Pirates', 'San Diego Padres', 'San Francisco Giants',
  'Seattle Mariners', 'St. Louis Cardinals', 'Tampa Bay Rays', 'Texas Rangers', 'Toronto Blue Jays',
  'Washington Nationals'
];

// Multi-Sport rows aligned by city/region (matches official break sheet)
const MULTI_SPORT_ROWS = [
  ['Arizona Cardinals', 'Phoenix Suns', 'Arizona Diamondbacks'],
  ['Atlanta Falcons', 'Atlanta Hawks', 'Atlanta Braves'],
  ['Baltimore Ravens', 'Sacramento Kings', 'Baltimore Orioles'],
  ['Buffalo Bills', 'Toronto Raptors', 'Toronto Blue Jays'],
  ['Carolina Panthers', 'Charlotte Hornets', ''],
  ['Chicago Bears', 'Chicago Bulls', 'Chicago Cubs'],
  ['Cincinnati Bengals', '', 'Cincinnati Reds'],
  ['Cleveland Browns', 'Cleveland Cavaliers', 'Cleveland Guardians'],
  ['Dallas Cowboys', 'Dallas Mavericks', 'Texas Rangers'],
  ['Denver Broncos', 'Denver Nuggets', 'Colorado Rockies'],
  ['Detroit Lions', 'Detroit Pistons', 'Detroit Tigers'],
  ['Green Bay Packers', 'Milwaukee Bucks', 'Milwaukee Brewers'],
  ['Houston Texans', 'Houston Rockets', 'Houston Astros'],
  ['Indianapolis Colts', 'Indiana Pacers', 'Chicago White Sox'],
  ['Jacksonville Jaguars', '', 'San Diego Padres'],
  ['Kansas City Chiefs', 'Oklahoma City Thunder', 'Kansas City Royals'],
  ['Las Vegas Raiders', 'Utah Jazz', 'Oakland Athletics'],
  ['Los Angeles Chargers', 'Los Angeles Clippers', 'Los Angeles Dodgers'],
  ['Los Angeles Rams', 'Los Angeles Lakers', 'Los Angeles Angels'],
  ['Miami Dolphins', 'Miami Heat', 'Miami Marlins'],
  ['Minnesota Vikings', 'Minnesota Timberwolves', 'Minnesota Twins'],
  ['New England Patriots', 'Boston Celtics', 'Boston Red Sox'],
  ['New Orleans Saints', 'New Orleans Pelicans', ''],
  ['New York Giants', 'New York Knicks', 'New York Yankees'],
  ['New York Jets', 'Brooklyn Nets', 'New York Mets'],
  ['Philadelphia Eagles', 'Philadelphia 76ers', 'Philadelphia Phillies'],
  ['Pittsburgh Steelers', 'San Antonio Spurs', 'Pittsburgh Pirates'],
  ['San Francisco 49ers', 'Golden State Warriors', 'San Francisco Giants'],
  ['Seattle Seahawks', 'Portland Trail Blazers', 'Seattle Mariners'],
  ['Tampa Bay Buccaneers', 'Orlando Magic', 'Tampa Bay Rays'],
  ['Tennessee Titans', 'Memphis Grizzlies', 'St. Louis Cardinals'],
  ['Washington Commanders', 'Washington Wizards', 'Washington Nationals'],
  ['Miscellaneous', '', ''],
];

// Lookup: NFL team name → { nfl, nba, mlb } from MULTI_SPORT_ROWS
const NFL_TO_MULTI = {};
for (const [nfl, nba, mlb] of MULTI_SPORT_ROWS) {
  if (nfl) NFL_TO_MULTI[nfl] = { nfl, nba, mlb };
}

export function buildSpotRows(category, spotType) {
  if (spotType === 'Division') {
    return [
      ...NFL_DIVISIONS.map(d => ({
        division: d.name,
        teams: d.teams,
        multiRows: d.teams.map(t => NFL_TO_MULTI[t] || { nfl: t, nba: '', mlb: '' }),
        nfl: '', nba: '', mlb: ''
      })),
      // No Miscellaneous row for Division breaks
    ];
  }
  if (category === 'Football') return NFL_TEAMS.map(t => ({ nfl: t, nba: '', mlb: '' }));
  if (category === 'Basketball') return NBA_TEAMS.map(t => ({ nfl: '', nba: t, mlb: '' }));
  if (category === 'Baseball') return MLB_TEAMS.map(t => ({ nfl: '', nba: '', mlb: t }));
  if (category === 'Multi-Sport') return MULTI_SPORT_ROWS.map(([nfl, nba, mlb]) => ({ nfl, nba, mlb }));
  return [];
}

// Download the Break Spots grid as a CSV: Customer, Price, NFL, NBA, MLB,
// Hit, Card(s), Freebie(s). Built from the full team grid (buildSpotRows) so
// every team row is included even when blank, then merged with saved spot
// data by sort_order — exactly how the page maps rows on load. Ignores the
// stats row. Hit is the checkbox (card text present); Card(s) is that text.
export function downloadSpotsCsv(brk, spots) {
  const category = brk?.category;
  const spotType = brk?.spot_type || 'Team';
  const grid = buildSpotRows(category, spotType);
  const byOrder = new Map();
  (spots || []).forEach(s => { if (s.sort_order != null) byOrder.set(s.sort_order, s); });

  const header = ['Customer', 'Price', 'NFL', 'NBA', 'MLB', 'Hit', 'Card(s)', 'Freebie(s)'];
  const cell = v => {
    const s = v == null ? '' : String(v);
    return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  const priceOf = s => (s == null || s.price == null || s.price === '') ? '' : Math.round(Number(s.price));

  const lines = [];
  grid.forEach((row, i) => {
    const s = byOrder.get(i);
    const customer = s?.customer || '';
    const price = priceOf(s);
    const hitFlag = s?.hit?.trim() ? 'Yes' : 'No';
    const cards = s?.hit || '';
    const freebie = s?.freebie || '';
    if (spotType === 'Division') {
      // Each division spans several team sub-rows; customer/price/hit/freebie
      // belong to the division and are shown once (mirrors the rowSpan layout).
      (row.multiRows || []).forEach((mr, j) => {
        lines.push(j === 0
          ? [customer, price, mr.nfl || '', mr.nba || '', mr.mlb || '', hitFlag, cards, freebie]
          : ['', '', mr.nfl || '', mr.nba || '', mr.mlb || '', '', '', '']);
      });
    } else {
      lines.push([customer, price, row.nfl || '', row.nba || '', row.mlb || '', hitFlag, cards, freebie]);
    }
  });

  const csv = [header, ...lines].map(r => r.map(cell).join(',')).join('\r\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Break ${brk?.break_number ?? ''} - Spots.csv`.replace(/\s+/g, ' ').trim();
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}
